import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

interface Step {
  id: string;
  label: string;
  path: string;
}

const MAIN_STEPS: Step[] = [
  { id: 'seleccion', label: 'Selección de Mascarilla', path: '/eleccion/respiracion' },
  { id: 'productos', label: 'Crea tu kit', path: '/eleccion/productos' },
  { id: 'carrito', label: 'Tu carrito', path: '/eleccion/carrito' }
];

export function MainProgressBar() {
  const location = useLocation();

  const { currentStepIndex, currentStepLabel, isTransitionState, isCurrentStepCompleted } = useMemo(() => {
    // Handle cotización routes - show as "Tu carrito" step (completed state)
    if (location.pathname.startsWith('/cotizacion/')) {
      return {
        currentStepIndex: 2, // Step 3 (Tu carrito)
        currentStepLabel: 'Tu carrito',
        isTransitionState: false,
        isCurrentStepCompleted: undefined
      };
    }
    
    // Handle medicion-resultado page: step 1 green, line 1-2 blue
    if (location.pathname === '/eleccion/medicion-resultado') {
      return {
        currentStepIndex: 0, // Step 1 (Selección de Mascarilla) completed
        currentStepLabel: '', // No title for transition state
        isTransitionState: false,
        isCurrentStepCompleted: 0
      };
    }
    
    // Handle felicitaciones page as transition state 1.5
    if (location.pathname === '/eleccion/felicitaciones') {
      return {
        currentStepIndex: 0, // Still on step 1 (Selección de Mascarilla)
        currentStepLabel: '', // No title for transition state
        isTransitionState: true
      };
    }
    
    // Handle plan selection as part of step 2 (Crea tu kit)
    if (location.pathname === '/eleccion/plan-seleccion') {
      return {
        currentStepIndex: 1, // Step 2 (Crea tu kit)
        currentStepLabel: 'Crea tu kit',
        isTransitionState: false
      };
    }
    
    // Handle medicion path as part of step 1 (Selección de Mascarilla)
    if (location.pathname === '/eleccion/medicion') {
      return {
        currentStepIndex: 0, // Step 1 (Selección de Mascarilla)
        currentStepLabel: 'Selección de Mascarilla',
        isTransitionState: false
      };
    }
    
    const currentIndex = MAIN_STEPS.findIndex(step => location.pathname === step.path);
    const currentStep = MAIN_STEPS[currentIndex];
    
    return {
      currentStepIndex: currentIndex >= 0 ? currentIndex : 0,
      currentStepLabel: currentStep?.label || MAIN_STEPS[0].label,
      isTransitionState: false
    };
  }, [location.pathname]);

  return (
    <div className="flex flex-col items-center space-y-2 my-2 max-w-full overflow-hidden">
      {/* Progress Circles */}
      <div className="flex items-center space-x-1 sm:space-x-2 max-w-full overflow-hidden px-2">
        {MAIN_STEPS.map((step, index) => {
          const isCompleted = index < currentStepIndex || (isTransitionState && index === currentStepIndex) || (isCurrentStepCompleted == index);
          const isCurrent = index === currentStepIndex && !isTransitionState;
          const isPainted = isCompleted || isCurrent;
          
          return (
            <div key={step.id} className="flex items-center flex-shrink-0">
              {/* Circle */}
              <div
                className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isCompleted
                    ? 'bg-success text-white'
                    : isCurrent
                    ? 'bg-primary text-white'
                    : 'bg-muted text-muted-foreground border-2 border-muted-foreground/20'
                }`}
              >
                {isCompleted ? (
                  <span className="text-sm font-medium">{index + 1}</span>
                  // <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </div>

              {/* Connecting Line */}
              {index < MAIN_STEPS.length - 1 && (
                <div
                  className={`w-3 sm:w-6 h-0.5 mx-0.5 sm:mx-1 transition-all duration-300 flex-shrink-0 ${
                    index < currentStepIndex || (isTransitionState && index === currentStepIndex)
                      ? 'bg-success'
                      : 'bg-muted'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Current Step Title */}
      {!isTransitionState && (
        <h1 className="text-lg font-semibold text-foreground text-center truncate max-w-full px-2">
          {currentStepLabel}
        </h1>
      )}
    </div>
  );
}